import React from "react";
import {
  Home,
  Users,
  Building2,
  Phone,
  PhoneCall,
  PhoneOutgoing,
  Play,
  Settings,
  HelpCircle,
  Shield,
} from "lucide-react";

import { AdminDashboard } from "@/pages/AdminDashboard";
import { UsersPage } from "@/pages/UsersPage";
import { CompaniesPage } from "@/pages/CompaniesPage";
import { CompanyDetailsPage } from "@/pages/CompanyDetailsPage";
import { CompanySessionsPage } from "@/pages/CompanySessionsPage";
import { NumbersPage } from "@/pages/NumbersPage";
import { SessionsPage } from "@/pages/SessionsPage";
import { SessionDetailsPage } from "@/pages/SessionDetailsPage";
import { PlaygroundPage } from "@/pages/PlaygroundPage";
import { PlaygroundPageExp } from "@/pages/PlaygroundPageExp";
import { OutboundCallPage } from "@/pages/OutboundCallPage";
import PermissionManagementPage from "@/pages/PermissionManagementPage";

import type { RouteConfig } from "@/lib/permissions";

/**
 * Centralized route configuration - Single source of truth for routes and menus
 */
export const ROUTE_CONFIGS: RouteConfig[] = [
  {
    path: "/admin",
    component: AdminDashboard,
    menu: {
      label: "Dashboard",
      icon: Home,
      group: "primary",
      order: 1,
    },
  },
  {
    path: "/admin/users",
    component: UsersPage,
    permissions: "manage_users",
    menu: {
      label: "Users",
      icon: Users,
      group: "primary",
      order: 2,
    },
  },
  {
    path: "/admin/companies",
    component: CompaniesPage,
    permissions: "manage_companies",
    menu: {
      label: "Companies",
      icon: Building2,
      group: "primary",
      order: 3,
    },
  },
  {
    path: "/admin/companies/:id",
    component: CompanyDetailsPage,
    permissions: "manage_companies",
    // No menu property means it won't show in menu
  },
  {
    path: "/admin/companies/:companyId/sessions",
    component: CompanySessionsPage,
    permissions: "manage_companies",
    // No menu property means it won't show in menu
  },
  {
    path: "/admin/numbers",
    component: NumbersPage,
    permissions: "manage_numbers",
    menu: {
      label: "Call Numbers",
      icon: Phone,
      group: "primary",
      order: 4,
    },
  },
  {
    path: "/admin/sessions",
    component: SessionsPage,
    permissions: ["manage_sessions", "view_sessions"],
    menu: {
      label: "Sessions",
      icon: PhoneCall,
      group: "primary",
      order: 5,
    },
  },
  {
    path: "/admin/sessions/:sessionId",
    component: SessionDetailsPage,
    permissions: ["manage_sessions", "view_sessions"],
    // No menu property means it won't show in menu
  },
  {
    path: "/admin/playground",
    component: PlaygroundPage,
    permissions: ["manage_playground", "use_playground"],
    menu: {
      label: "Playground",
      icon: Play,
      group: "primary",
      order: 6,
    },
  },
  {
    path: "/admin/playground-exp",
    component: PlaygroundPageExp,
    permissions: ["manage_playground", "use_playground"],
    menu: {
      label: "Playground Exp",
      icon: Play,
      group: "primary",
      order: 7,
    },
  },
  {
    path: "/admin/outbound-call",
    component: OutboundCallPage,
    permissions: ["manage_sessions", "use_playground"],
    menu: {
      label: "Outbound Call",
      icon: PhoneOutgoing,
      group: "primary",
      order: 8,
    },
  },
];

/**
 * Secondary menu configurations (settings, help, etc.)
 */
/**
 * Placeholder components for future pages
 */
const SettingsPage: React.FC = () => {
  return React.createElement("div", null, "Settings Page (Coming Soon)");
};

const HelpPage: React.FC = () => {
  return React.createElement("div", null, "Help Page (Coming Soon)");
};

export const SECONDARY_ROUTE_CONFIGS: RouteConfig[] = [
  {
    path: "/admin/settings",
    component: SettingsPage,
    permissions: "manage_settings",
    menu: {
      label: "Settings",
      icon: Settings,
      group: "secondary",
      order: 1,
    },
  },
  {
    path: "/admin/permissions",
    component: PermissionManagementPage,
    permissions: "manage_settings",
    menu: {
      label: "Permissions",
      icon: Shield,
      group: "secondary",
      order: 2,
    },
  },
  {
    path: "/admin/help",
    component: HelpPage,
    menu: {
      label: "Get Help",
      icon: HelpCircle,
      group: "secondary",
      order: 3,
    },
  },
];

/**
 * Get all route configurations
 */
export function getAllRouteConfigs(): RouteConfig[] {
  return [...ROUTE_CONFIGS, ...SECONDARY_ROUTE_CONFIGS];
}

/**
 * Get route configurations filtered by group
 */
export function getRouteConfigsByGroup(
  group: "primary" | "secondary"
): RouteConfig[] {
  return getAllRouteConfigs().filter((route) => route.menu?.group === group);
}

/**
 * Get menu-visible route configurations
 */
export function getMenuRouteConfigs(): RouteConfig[] {
  return getAllRouteConfigs()
    .filter((route) => route.menu) // Only routes with menu configuration
    .sort((a, b) => (a.menu?.order || 0) - (b.menu?.order || 0));
}

/**
 * Find route configuration by path
 */
export function findRouteConfig(path: string): RouteConfig | undefined {
  return getAllRouteConfigs().find((route) => route.path === path);
}
